import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { show } from 'store/modules/dialog/actions';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Form } from '@unform/web';
import { useNavigate, useLocation } from 'react-router-dom';

import api from 'services/api';

import Card from 'components/Card';
import Button from 'components/Button';
import Widget from 'components/Widget';
import Tooltip from 'components/Tooltip';
import Pagination from 'components/Pagination';
import DropDownMenu from 'components/DropDownMenu';
import PlayerBaixa from 'components/PlayerBaixa';
import Input from 'components/Input';
import Avatar from 'components/Avatar';
import Loader from 'components/ContentLoader/GenericLoader';
import {
  FiBookOpen,
  FiAlertTriangle,
  FiCheckCircle,
  FiCoffee,
  FiPlus,
  FiEdit,
  FiSearch,
  FiTrash2,
  FiLink,
  FiCopy,
  FiInfo,
  FiHeadphones,
  FiCheckSquare,
} from 'react-icons/fi';
import NoSpot from 'assets/Music-bro.svg';

import { Container, WidgetContent, TdFlag, Table } from './styles';
import { Radio, SpotRadio } from 'pages/@types';
import AppError from 'utils/AppError';
import NotFound from 'components/NotFound';
import { Status } from 'utils/variables';
import Flag from 'components/Flag';

interface Workflow {
  emprocesso: number;
  aprovacao: number;
  alteracao: number;
  aprovado: number;
}

export default function Spots() {
  // react-router-dom
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * USE STATE
   */
  const [workflow, setWorkflow] = useState({} as Workflow);
  const [filtro, setFiltro] = useState('');
  const [spots, setSpots] = useState<SpotRadio[]>([]);
  const [selected, setSelected] = useState({} as SpotRadio);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(
    location.pathname.includes('/aprovacao') ? 'aprovacao' : 'emprocesso',
  );
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  /**
   * USE CALLBACK
   */

  const copyToClipboard = useCallback(text => {
    const dummy = document.createElement('textarea');
    // to avoid breaking orgain page when copying more words
    // cant copy when adding below this code
    // dummy.style.display = 'none'
    document.body.appendChild(dummy);
    // Be careful if you use texarea. setAttribute('value', value), which works with "input" does not work with "textarea". – Eduard
    dummy.value = text;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
    toast.success('Link copiado na a área de transferência');
  }, []);

  const handleStatusWidget = useCallback(s => {
    setStatus(s);
    setPage(1);
  }, []);

  const handlePlayer = useCallback(
    previewurl => {
      if (previewurl) {
        setPlayer({
          open: !player.open,
          midia_url: previewurl,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );
  const handleDelete = useCallback(
    sp => {
      setSelected(sp);
      const optionBase = {
        type: 'question',
        contentHTML: `Remover <b>${sp.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/spotradio/${sp.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Spot removido!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleInfoDialog = useCallback(
    (spot: SpotRadio) => {
      const contentHTML = spot
        ? `<p>Retranca: ${spot.retranca}</p>
    <p>Status: ${spot.status}</p>
    <p>Duração: ${spot.duracao}</p>
    <p>Período de veiculação: De: ${
      spot.datainicio
        ? format(new Date(spot.datainicio), 'dd/MM/yyyy')
        : 'Indeterminado'
    }. Até: ${
            spot.datafim
              ? format(new Date(spot.datafim), 'dd/MM/yyyy')
              : 'Indeterminado'
          } </p>
    <p>Rádios: ${
      spot.radios && spot.radios.length > 0
        ? spot.radios.map(r => r.nome).join()
        : 'Indeterminado'
    }</p>
    <p>Observação: ${spot.observacao ? spot.observacao : `Sem observação`}</p>`
        : `<p>Informações pendentes<p>`;
      const options = {
        type: 'info',
        contentHTML,
        showCancelButton: false,
        showConfirmButton: true,
        confirmButtonText: 'FECHAR',
      };
      dispatch(show(options));
    },
    [dispatch],
  );
  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const handleCopy = useCallback(
    sp => {
      const optionBase = {
        type: 'question',
        contentHTML: `Duplicar <b>${sp.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        inputType: 'text',
        inputValue: sp.retranca,
        callback: {
          url: `/spotradio`,
          method: api.post,
          data: {
            duracao: sp.duracao,
            datainicio: sp.datainicio,
            datafim: sp.datafim,
            observacao: sp.observacao,
            radios: sp.radios ? sp.radios.map((r: Radio) => r.id) : null,
          },
          action: 'duplica_spot',
        },
        haveCallback: true,
        successMessage: `Spot ${sp.retranca} duplicado!`,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      setSelected(sp);
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleActionSelect = useCallback(
    (sp, action) => {
      switch (action.value) {
        case 1:
          handleInfoDialog(sp);
          break;
        case 2:
          handleDelete(sp);
          break;
        case 3:
          copyToClipboard(sp.filepath_url);
          break;
        default:
          break;
      }
    },
    [copyToClipboard, handleDelete, handleInfoDialog],
  );

  /**
   * USE EFFECT
   */

  useEffect(() => {
    const controller = new AbortController();
    async function loadSpot() {
      setLoading(true);
      try {
        const response = await api.get('/spotradio', {
          signal: controller.signal,
          params: {
            search: filtro.toUpperCase(),
            status,
            page,
          },
        });
        if (response && response.data) {
          setSpots(response.data.spots);
          setPages(response.data.pages);
          setWorkflow(response.data.workflow);
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadSpot();
    return () => {
      controller.abort();
    };
  }, [page, status, filtro]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'duplica_spot' && selected) {
        // atualiza adicionando no workflow
        setWorkflow(prevState => ({
          ...prevState,
          emprocesso: prevState.emprocesso + 1,
        }));
        // Atualiza tabela adicionando o duplicado
        if (status === Status.EMPROCESSO)
          setSpots(prevState => [...prevState, callbackDialog.success.data]);
      } else if (callbackDialog.success.action === 'success' && selected) {
        // Atualiza widgets removendo a solicitacao deletada
        setWorkflow(prevState => ({
          emprocesso:
            selected.status === Status.EMPROCESSO
              ? prevState.emprocesso - 1
              : prevState.emprocesso,
          aprovacao:
            selected.status === Status.APROVACAO
              ? prevState.aprovacao - 1
              : prevState.aprovacao,
          alteracao:
            selected.status === Status.ALTERACAO
              ? prevState.alteracao - 1
              : prevState.alteracao,
          aprovado:
            selected.status === Status.APROVADO
              ? prevState.aprovado - 1
              : prevState.aprovado,
        }));
        // Atualiza tabela removendo a solicitacao deletada
        setSpots(prevState => prevState.filter(sp => sp.id !== selected.id));
      }
    }
  }, [callbackDialog, selected, status]);

  return (
    <Container>
      <WidgetContent>
        <Widget
          title="Em processo"
          type="condensed"
          icon={<FiBookOpen />}
          showLink={false}
          active={status === Status.EMPROCESSO}
          color="info"
          loading={loading}
          handleClick={() => handleStatusWidget(Status.EMPROCESSO)}
          data={workflow.emprocesso ? workflow.emprocesso : 0}
        />
        <Widget
          title="Aprovação"
          type="condensed"
          icon={<FiCoffee />}
          active={status === Status.APROVACAO}
          color="warn"
          loading={loading}
          handleClick={() => handleStatusWidget(Status.APROVACAO)}
          data={workflow.aprovacao ? workflow.aprovacao : 0}
        />
        <Widget
          title="Alteração"
          type="condensed"
          icon={<FiAlertTriangle />}
          active={status === Status.ALTERACAO}
          color="danger"
          loading={loading}
          handleClick={() => handleStatusWidget(Status.ALTERACAO)}
          data={workflow.alteracao ? workflow.alteracao : 0}
        />
        <Widget
          title="Aprovado"
          type="condensed"
          icon={<FiCheckCircle />}
          active={status === Status.APROVADO}
          color="success"
          loading={loading}
          handleClick={() => handleStatusWidget(Status.APROVADO)}
          data={workflow.aprovado ? workflow.aprovado : 0}
        />
      </WidgetContent>
      <Card>
        <header style={{ alignItems: 'baseline' }}>
          <h4>LISTA DE SPOTS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            href={'/spotradio/novo'}
            tooltip="Criar novo Spot"
          >
            NOVO SPOT
            <FiPlus />
          </Button>
          <div>
            <Form
              onSubmit={e => {
                setFiltro(e.searchLocal);
                setPage(1);
              }}
            >
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader type="spot_radio" />
        ) : spots && spots.length ? (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>RETRANCA</th>
                  <th>STATUS ASSINATURA</th>
                  <th>DUR.</th>
                  <th>EDITOR</th>
                  <th>AVALIADOR</th>
                  <th>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {spots.map((sp, index) => (
                  <tr key={sp.id}>
                    <TdFlag type={sp.status} hasMidia={!!sp.filename}>
                      <div id={`tdFlag_id${index}`} />
                      <Tooltip target={`tdFlag_id${index}`} position="right">
                        <p>
                          <strong>STATUS:</strong> {sp.status}
                          {!sp.filename && ' (Sem áudio)'}
                        </p>
                      </Tooltip>
                    </TdFlag>
                    <td>{sp.retranca}</td>
                    <td>
                      <Flag
                        name={sp.statusassinatura}
                        type={sp.statusassinatura}
                      />
                    </td>
                    <td>{sp.duracao} s</td>
                    <td>
                      <Avatar
                        size="40"
                        name={sp.editor?.nome}
                        src={sp.editor?.avatar_url}
                        round
                      />
                    </td>
                    <td>
                      <Avatar
                        size="40"
                        name={sp.avaliador?.nome}
                        src={sp.avaliador?.avatar_url}
                        round
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex' }}>
                        <Button
                          id={`id_escutar_${sp.id}`}
                          fill
                          color="font"
                          round
                          disabled={!sp.filepath_url}
                          onClick={() => handlePlayer(sp.filepath_url)}
                        >
                          <FiHeadphones />
                        </Button>
                        <Tooltip target={`id_escutar_${sp.id}`}>
                          ESCUTAR
                        </Tooltip>
                        {[Status.EMPROCESSO, Status.ALTERACAO].includes(
                          status,
                        ) && (
                          <>
                            <Button
                              id={`id_edit_${sp.id}`}
                              fill
                              color="font"
                              round
                              onClick={() => navigate(`/spotradio/${sp.id}`)}
                            >
                              <FiEdit />
                            </Button>
                            <Tooltip target={`id_edit_${sp.id}`}>
                              EDITAR
                            </Tooltip>
                            <Button
                              id={`id_duplicar_${sp.id}`}
                              fill
                              color="font"
                              round
                              onClick={() => handleCopy(sp)}
                            >
                              <FiCopy />
                            </Button>
                            <Tooltip target={`id_duplicar_${sp.id}`}>
                              DUPLICAR
                            </Tooltip>
                          </>
                        )}
                        {status === Status.APROVACAO && (
                          <>
                            <Button
                              id={`id_avaliar_${sp.id}`}
                              fill
                              color="font"
                              round
                              onClick={() => navigate(`/spotradio/${sp.id}`)}
                            >
                              <FiCheckSquare />
                            </Button>
                            <Tooltip target={`id_avaliar_${sp.id}`}>
                              AVALIAR
                            </Tooltip>
                          </>
                        )}
                        <DropDownMenu
                          disabled={false}
                          id={`acoes${index}`}
                          actions={[
                            {
                              value: 1,
                              label: 'INFO',
                              icon: <FiInfo />,
                            },
                            {
                              value: 2,
                              label: 'APAGAR',
                              disabled: sp.status === Status.APROVADO,
                              icon: <FiTrash2 />,
                            },
                            {
                              value: 3,
                              label: 'COPIAR LINK',
                              disabled: !sp.filename,
                              icon: <FiLink />,
                            },
                          ]}
                          handleClick={action =>
                            !action.disabled && handleActionSelect(sp, action)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NotFound img={NoSpot} title="Spots">
            Nenhum SPOT encontrado
          </NotFound>
        )}
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
        type="audio"
      />
    </Container>
  );
}
